/**
 * react-flow host that renders an ELK-layouted CircuitIR with
 * per-ComponentKind symbol rendering.
 *
 * The IR layer + ELK layout produce abstract `(x, y, width, height)`
 * per node and `sections[]` per edge; this component:
 *
 *   * Maps each ELK node back to its `Component` via the IR lookup,
 *   * Sets `node.type` from `Component.kind` so react-flow renders the
 *     matching symbol (resistor zigzag, cap parallel lines, IC body, …),
 *   * Maps edges → react-flow Edge with the phosphor-cyan stroke.
 *
 * Kind dispatch lives in `nodes/index.ts` via `circuitNodeTypes`.
 * Anything not registered there falls through to the GenericNode
 * fallback (rendered with a dashed border so it's visually obvious
 * the kind wasn't recognised).
 */
import {
  Background,
  Controls,
  type Edge,
  type EdgeTypes,
  type Node,
  ReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import { OrthogonalEdge } from "@lib/elk/OrthogonalEdge";
import {
  type ElkLayoutedEdge,
  type ElkLayoutedGraph,
  type ElkLayoutedNode,
  useElkLayout,
} from "@lib/elk/useElkLayout";
import type { CircuitIR } from "@models/circuit-ir";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMemo } from "react";

import { circuitNodeTypes } from "./nodes";

interface CircuitCanvasProps {
  ir: CircuitIR;
  /** Pixel height for the canvas. Defaults to 480 — enough for a
   *  ~30-component preview without scrolling. */
  height?: number;
  /** Component ids (UUIDs) to render with a phosphor-cyan halo. Used
   *  by the RenameTheatreDialog and SnapshotDiffDialog where the
   *  caller already has UUIDs in hand. */
  highlightIds?: ReadonlySet<string>;
  /** Component refdes to halo. Used by surfaces that work in refdes
   *  (BOM tab, DRC drawer) — resolved to UUIDs internally via the IR
   *  lookup so callers don't have to. Combined with `highlightIds`
   *  via union, so both can be passed simultaneously. */
  highlightRefdes?: ReadonlySet<string>;
}

export function CircuitCanvas({
  ir,
  height = 480,
  highlightIds,
  highlightRefdes,
}: CircuitCanvasProps) {
  const theme = useTheme();
  const { graph, isLoading, error } = useElkLayout(ir, {
    direction: "RIGHT",
    includeOrphans: false,
  });

  // Component lookup so the ELK→react-flow mapper can read kind / refdes
  // / value off the original IR rather than burning bytes carrying them
  // through ELK.
  const componentById = useMemo(() => {
    const m = new Map(ir.components.map((c) => [c.id, c]));
    return m;
  }, [ir.components]);

  // Merge UUID + refdes highlight sets into a single id set for the
  // mapper. Resolving refdes → id is O(n) over components per render —
  // cheap; the canvas already indexes them in `componentById`.
  const mergedHighlightIds = useMemo(() => {
    if (!highlightRefdes && !highlightIds) return undefined;
    const out = new Set<string>(highlightIds ?? []);
    if (highlightRefdes && highlightRefdes.size > 0) {
      for (const c of ir.components) {
        if (highlightRefdes.has(c.refdes)) out.add(c.id);
      }
    }
    return out;
  }, [ir.components, highlightIds, highlightRefdes]);

  const { nodes, edges } = useMemo(
    () =>
      toReactFlow(
        graph,
        componentById,
        theme.palette.accent.main,
        mergedHighlightIds,
      ),
    [graph, componentById, theme.palette.accent.main, mergedHighlightIds],
  );

  if (isLoading && !graph) {
    return (
      <Box
        sx={(t) => ({
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: t.palette.surface.sunken,
          borderRadius: 1,
        })}
      >
        <CircularProgress size={20} />
        <Typography variant="caption" sx={{ ml: 1.5, color: "text.secondary" }}>
          Laying out {ir.components.length} components…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "error.main",
        }}
      >
        <Typography variant="body2">Layout failed: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={(t) => ({
        height,
        bgcolor: t.palette.surface.sunken,
        borderRadius: 1,
        // The blueprint backdrop lives on the surrounding container; the
        // react-flow canvas itself stays transparent so the texture
        // shows through the controls / minimap underlays.
        "& .react-flow": { background: "transparent" },
        "& .react-flow__controls": {
          background: t.palette.surface.raised,
          border: `1px solid ${t.palette.border.default}`,
        },
        "& .react-flow__controls button": {
          background: "transparent",
          color: t.palette.text.secondary,
          borderColor: t.palette.border.default,
          "&:hover": { color: t.palette.primary.main },
        },
      })}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={circuitNodeTypes}
        edgeTypes={schematicEdgeTypes}
        fitView
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable
      >
        <Background gap={16} size={1} color={theme.palette.border.default} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </Box>
  );
}

function toReactFlow(
  graph: ElkLayoutedGraph | null,
  componentById: Map<string, CircuitIR["components"][number]>,
  edgeStroke: string,
  highlightIds: ReadonlySet<string> | undefined,
): { nodes: Node[]; edges: Edge[] } {
  if (!graph?.children) return { nodes: [], edges: [] };
  const nodes: Node[] = graph.children.map((c) =>
    nodeFromElk(c, componentById, highlightIds?.has(c.id) ?? false),
  );
  const edges: Edge[] = (graph.edges ?? []).map((e) =>
    edgeFromElk(e, edgeStroke),
  );
  return { nodes, edges };
}

function nodeFromElk(
  node: ElkLayoutedNode,
  componentById: Map<string, CircuitIR["components"][number]>,
  highlight: boolean,
): Node {
  const component = componentById.get(node.id);
  // The kind drives nodeTypes dispatch — fall through to "other" if the
  // component isn't in the lookup (unlikely but defensive).
  const type = component?.kind ?? "other";
  return {
    id: node.id,
    type,
    position: { x: node.x ?? 0, y: node.y ?? 0 },
    data: {
      refdes: component?.refdes,
      value: component?.value,
      kind: component?.kind,
      pinCount: component?.pins.length ?? 0,
      highlight,
    },
    width: node.width ?? 100,
    height: node.height ?? 60,
    // Strip default react-flow styles — the symbol component owns its
    // own visual; an ambient background would clip the SVG.
    style: { background: "transparent", border: "none", padding: 0 },
  };
}

function edgeFromElk(edge: ElkLayoutedEdge, stroke: string): Edge {
  return {
    id: edge.id,
    source: stripPort(edge.sources[0]),
    target: stripPort(edge.targets[0]),
    // Custom orthogonal renderer — consumes ELK's `sections[]`
    // (the bend points ELK already computed when it laid out the
    // graph with `edgeRouting: ORTHOGONAL`). Without this, react-
    // flow's default edge ignored ELK's geometry and recomputed a
    // bezier path from source/target endpoints, which is what
    // made the dense schematic look like a noodle bowl.
    type: "orthogonal",
    data: { sections: edge.sections },
    animated: false,
    style: { stroke, strokeWidth: 1.25, opacity: 0.8 },
  };
}

/** react-flow `edgeTypes` registry — module-level constant so the
 *  identity is stable across renders (re-creating it on every
 *  render makes react-flow tear down + reattach edges). */
const schematicEdgeTypes: EdgeTypes = {
  orthogonal: OrthogonalEdge,
};

function stripPort(portId: string): string {
  const idx = portId.indexOf("::");
  return idx === -1 ? portId : portId.slice(0, idx);
}
